import { Getter, Setter } from '@dhmk/zustand-lens';
import { GridRowId, GridTreeNode } from '@mui/x-data-grid-premium';
import _ from 'lodash';
import { SafeParseError, ZodError, z } from 'zod';

export type GridRow<T = object> = Partial<T> & {
  status: GridRowStatus;
  rowId: GridRowId;
  id?: number;
  isDraft?: boolean;
  isDeleted?: boolean;
  isCreating?: boolean;
};
export type GridRowStatus = 'draft' | 'loading' | 'accepted' | 'rejected' | 'deleting';
export type GridStoreUpdateAction = 'create' | 'update' | 'delete' | 'restore';

/**
 * A draft row with no reconciled id and no create already in flight is a fresh create.
 * Editable grids MUST branch on this — not the MUI grid's lagging `row.status` — so a
 * single draft commit can't fire two `createLineItem`s and persist a "ghost" twin (TAS-3271).
 * Single source of truth for every editable grid; keep the create/update decision here.
 */
export const isDraftAwaitingCreate = (row?: GridRow): boolean =>
  Boolean(row?.isDraft) && _.isNil(row?.id) && !row?.isCreating;

/**
 * Context a row-dependent validation model is resolved against.
 *
 *   row      always present — the row being validated
 *   rowNode  MUI tree node, present ONLY when a grid resolves the model
 *            (EditableDataGrid binds it from apiRef); undefined when the
 *            store is driven headlessly (tests, direct `edit` callers).
 *            Row-dependent models MUST tolerate its absence.
 */
export type GridValidationContext<T = object> = { row: GridRow<T>; rowNode?: GridTreeNode };

/**
 * A validation model is EITHER one schema for every row, OR a function of the row.
 *
 *   z.ZodType            ── static      ── 8 of 10 grids
 *   (ctx) => z.ZodType   ── row-dependent ── ExpensesGrid (rowNode.depth),
 *                                            InventoryTransferGrid (lot capacity)
 *
 * The union is resolved PER ROW at the point of use — `validateRows` (every
 * local row) and `edit` (the row being saved) — so both forms observe the
 * same row state. Static callers are unaffected: `z.ZodType` is still valid.
 */
export type GridValidationModel<T = object> = z.ZodType<T> | ((ctx: GridValidationContext<T>) => z.ZodType<T>);

export const resolveValidationModel = <T>(
  validationModel: GridValidationModel<T>,
  context: GridValidationContext<T>
): z.ZodType<T> => (typeof validationModel === 'function' ? validationModel(context) : validationModel);

export type GridRowState<T = object> = {
  initialRows: GridRow<T>[];
  localRows: Record<GridRowId, GridRow<T>>;
  nextRowId: number;
  rowSnapshots: Record<number, GridRow<T>>;
  rowPatches: Record<number, Partial<GridRow<T>>>;
  /**
   * Row-level save/validation failures. Holds ANY `Error` — `validateRows` and `edit`
   * park a `ZodError`, but a rejected mutation parks whatever the server threw. Narrow
   * with `instanceof ZodError` before reaching for `.issues` (ruling R3, 2026-08-02).
   */
  rowErrors: Record<number, Error>;
  // Used for tracking visited columns, and whether this row has been submitted by the user.
  rowInteractionModel: Record<number, { saved: boolean; visited: Set<string> }>;
  // these row updates will be consumed by the data grid api, in favor
  // of passing it the local rows directly
  rowUpdates: { rowId: GridRowId; _action: GridStoreUpdateAction }[];
};

export type GridRowEditEvent<T, P = Partial<GridRow<T>>> = {
  rowId: GridRowId;
  resolve: (accepted: T) => void;
  reject: (error: Error) => void;
  payload: P;
  getLatestId: () => number;
};

export type GridRowRemoveEvent = {
  rowId: GridRowId;
  payload: () => number;
  resolve: () => void;
  reject: (error: Error) => void;
};

export type GridRowActions<T extends object = object> = {
  // optimistic w/ api updates
  getNextRowId: () => number;
  getLocalRow(rowId: GridRowId): GridRow<T>;
  getLocalRows(): GridRow<T>[];
  // draft item should be considered "clean"
  addDraft(draftItem: T): GridRow<T>;
  edit(
    item: GridRow<T>,
    settings: { validationModel?: GridValidationModel<T>; forceEdit?: boolean }
  ): GridRowEditEvent<T>;
  reconcileEdit(rowId: GridRowId, accepted: T): void;
  remove(rowId: GridRowId): GridRowRemoveEvent;
  validateRows(validationModel: GridValidationModel<T>): void;
  /** Re-run the last validation model against current rows (after phased hydration adds late relations). */
  revalidate(): void;
  // synchronous local updates
  setLocalRow(item: GridRow<T>): void;
  assignToLocalRows(updateGetter: (item: GridRow<T>) => Partial<T>): void;
  deleteLocal(rowId: GridRowId, lastCheck?: boolean): boolean;
  recordPatch(row: GridRow<T>, patch: Partial<GridRow<T>>): void;
  recordSnapshot(rowId: GridRowId, row: GridRow<T>): void;
  /**
   * Cold-start seed. Replaces the ENTIRE row set and drops all per-row bookkeeping
   * (patches, errors, updates, interaction model). Not a merge — a warm merge is the
   * caller's job (see `initializeFromLightData` in lineItems.store.ts).
   */
  hydrateRows(rows: T[]): void;
  getPatch(rowId: GridRowId): Partial<GridRow<T>>;
  /** `null` clears the row's error. Any `Error` is accepted — see `rowErrors`. */
  setRowError(rowId: GridRowId, error: Error | null): void;
  /** `null` when the row is clean (pinned by the store contract spec). ZodError is preferred since we can get the specific path. */
  getRowError(rowId: GridRowId): Error | null;
  getRemoteRowId(rowId: GridRowId): GridRowId | null;
  syncRowUpdates(): { rowUpdates: { rowId: GridRowId; _action: GridStoreUpdateAction }[]; reset: () => void };
  recordInteraction(rowId: GridRowId, interaction: { visited?: string; saved?: boolean }): void;
  restoreDeletedRow(rowId: GridRowId, accepted: T & { deletedAt?: Date | null }): void;
  findRow(predicate: (row: GridRow<T>) => boolean): GridRow<T> | undefined;
};

type CanAddEntity<T = unknown> = (row: Partial<T>) => boolean;
type CanEditEntity<T = unknown> = (row: Partial<T>) => boolean;
type CanDeleteEntity<T = unknown> = (row: Partial<T>) => boolean;

export const EDITABLE_GRID_SLICE = 'editableGridInternal';

export type EditableGridState<T = object> = GridRowState<T> & GridRowActions<T>;

const generateRowIds = (rows: Array<any>) => {
  const sortedRows = [...rows].sort((a, b) => a.id - b.id);

  return sortedRows.map((row, index) => ({ ...row, rowId: index }));
};

// MUI's row-update VERB, never row data. `setLocalRow` stamps `_action` onto every
const GRID_INTERNAL_KEYS: ReadonlySet<string> = new Set(['_action']);

// "No value at all": absent, `null`, or an object whose every value is itself
const isNothing = (value: unknown): boolean =>
  _.isNil(value) || (_.isPlainObject(value) && Object.values(value as Record<string, unknown>).every(isNothing));

export const compare = <R = any, T = any>(original: T, updated: R) => {
  const compareKeys = new Set(Object.keys(original).concat(Object.keys(updated)));
  const updates: Partial<R> = {};

  compareKeys.forEach((key) => {
    if (GRID_INTERNAL_KEYS.has(key)) {
      return;
    }

    // if we want to remove something, we should set it to null.
    if (updated[key] === undefined) {
      return;
    }

    // ABSENT and NULLISH are the same "no value", so a valueSetter replay that
    if (isNothing(updated[key]) && isNothing(original[key])) {
      return;
    }

    if (!_.isEqual(original[key], updated[key])) {
      updates[key] = updated[key];
    }
  });

  return updates;
};

/**
 * Prototype check, not `keys.length` alone: a Date / Decimal / class instance keeps its
 * data OFF the own-keys list and is a real value.
 */
const isFieldlessObject = (value: unknown): boolean =>
  typeof value === 'object' &&
  value !== null &&
  Object.getPrototypeOf(value) === Object.prototype &&
  Object.keys(value).length === 0;

/**
 * Is this diff worth a network round trip?
 *
 *   {}                     ──▶ empty   nothing changed
 *   { accountLevel: {} }   ──▶ empty   a member that carries no field   ◄── G1
 *   { expenseType: null }  ──▶ REAL    null is the documented "remove"
 *   { tags: [] }           ──▶ REAL    an emptied list is a value
 *   { amount: 0 }          ──▶ REAL
 *   { date: <Date> }       ──▶ REAL    no OWN keys, but not a plain object
 *
 * `compare` copies whole values, so a member that is itself an empty plain object
 * says exactly as much as no member at all. A save built from such a diff reaches
 * the server as `input:{}` / `input:{accountLevel:{}}` — HTTP 200, zero effect,
 * and the user's edit silently gone (Jam 8d3c9116).
 */
export const isEmptyDiff = (diff: object): boolean => Object.values(diff).every(isFieldlessObject);

export type EditableGridInitialState<T = object, I = object> = I & {
  rows: T[];
  /**
   * The initial validation model — static, or resolved per row (see GridValidationModel).
   */
  validationModel: GridValidationModel<T>;
  // WIP... stubs for now
  capabilities: { canAddEntity: CanAddEntity; canEditEntity: CanEditEntity; canDeleteEntity: CanDeleteEntity };
};

export const createGridRowsSlice = <T, I>(
  set: Setter<EditableGridState<T>>,
  get: Getter<EditableGridState<T>>,
  { rows, capabilities }: EditableGridInitialState<T, I>
): EditableGridState<T> => {
  const _canAddEntity: CanAddEntity = (row) => true;
  const canEditEntity: CanEditEntity = (row) => true;
  const canDeleteEntity: CanDeleteEntity = (row) => true;
  // Last model passed to validateRows, so revalidate() can re-run it without re-threading.
  // Held unresolved: a row-dependent model must re-resolve against the CURRENT rows.
  let lastValidationModel: GridValidationModel<T> | undefined;
  const key = (row: GridRow<object>) => row.rowId;
  const initialRows = _.keyBy(generateRowIds(rows), key);
  return {
    localRows: initialRows,
    rowSnapshots: initialRows,
    rowPatches: {},
    rowErrors: {},
    rowUpdates: [],
    nextRowId: rows.length,
    initialRows: Object.values(initialRows),
    rowInteractionModel: Object.keys(initialRows).reduce(
      (acc, key) => {
        acc[key] = { saved: true, visited: new Set() };
        return acc;
      },
      {} as GridRowState['rowInteractionModel']
    ),
    hydrateRows: (rows: T[]) => {
      const seeded = _.keyBy(generateRowIds(rows), key);

      set((state) => {
        state.localRows = seeded as typeof state.localRows;
        state.rowSnapshots = seeded as typeof state.rowSnapshots;
        state.initialRows = Object.values(seeded) as typeof state.initialRows;
        state.nextRowId = rows.length;
        state.rowPatches = {} as typeof state.rowPatches;
        state.rowErrors = {} as typeof state.rowErrors;
        state.rowUpdates = [];
        state.rowInteractionModel = Object.keys(seeded).reduce(
          (acc, rowKey) => {
            acc[Number(rowKey)] = { saved: true, visited: new Set() };
            return acc;
          },
          {} as GridRowState['rowInteractionModel']
        );
      });
    },
    getNextRowId: () => {
      set((state) => {
        state.nextRowId++;
      });

      return get().nextRowId;
    },
    addDraft: (row) => {
      const store = get();
      const local: GridRow<T> = { ...row, status: 'draft', isDraft: true, rowId: get().getNextRowId() };

      store.setLocalRow(local);

      return local;
    },
    /* if the user deletes a draft row as it's saving, we need to restore it locally. */
    restoreDeletedRow: (rowId, accepted) => {
      const store = get();

      if (!accepted || accepted.deletedAt) {
        return;
      }

      store.setLocalRow({
        ...accepted,
        rowId,
        status: 'accepted',
        isDraft: false,
        isCreating: false,
        isDeleted: false,
      });
    },
    reconcileEdit: (rowId, accepted) => {
      const store = get();
      const current = store.getLocalRow(rowId);
      const rowError = store.rowErrors[rowId];

      if (!current) {
        console.warn('Tried to resolve on a row that no longer exists', rowId);
        store.restoreDeletedRow(rowId, accepted);
        return;
      }

      if (!accepted) {
        console.warn('Tried to resolve with no accepted changes', rowId);
        return;
      }

      if (current.isDeleted) {
        console.warn('Made updates to a row, but its since been deleted.', rowId);
        return;
      }

      // Only a ZodError carries per-path issues; a rejected mutation parks a plain Error
      // and has nothing to strip. Iterating it unconditionally threw (ruling R3).
      if (rowError instanceof ZodError) {
        for (const issue of rowError.issues) {
          if (issue.fatal) {
            _.unset(accepted, issue.path.join('.'));
          }
        }
      }

      /**
       * Handle folding in the changes from the api response into the current draft:
       *
       * 1. Get the changes to the (possibly outdated) row that triggered this response
       * 2. Apply them to the current local row
       * 3. Apply any changes from between the last response
       */
      const patch = compare(current, accepted);
      const appliedResponse = Object.assign(structuredClone(current), patch);
      const appliedChanges = Object.assign(appliedResponse, store.getPatch(rowId), {
        status: 'accepted',
        isDraft: false,
        isCreating: false,
      });

      store.recordSnapshot(rowId, appliedChanges);
      store.setLocalRow(appliedChanges);
    },
    edit: (newRow, { validationModel, forceEdit }) => {
      const store = get();
      const rowId = key(newRow);
      const original = store.localRows[rowId];

      if (!original) {
        return;
      }

      let local = null;

      if (!canEditEntity(newRow)) {
        console.debug('Cannot edit entity', rowId);
        return;
      }

      let changes = compare(original, newRow);

      store.recordPatch(newRow, changes);

      // A diff whose every member is a fieldless object says nothing — treat it as the
      // no-change it is, HERE, above every side effect, so the row never takes a
      // `loading` status for a request that would carry nothing (`isEmptyDiff`).
      const hasChanges = !isEmptyDiff(changes);

      const syncWholeRow = forceEdit || (original.isDraft && !original.isCreating);

      // Validate BEFORE the no-changes gate. `setRowError` is what paints a
      const rowUnderEdit = { ...newRow, status: 'loading' as const, isCreating: original.isDraft };
      const editValidationModel = validationModel && resolveValidationModel(validationModel, { row: rowUnderEdit });
      const editValidation = editValidationModel?.safeParse(rowUnderEdit);

      if (editValidation && !editValidation.success) {
        store.setRowError(rowId, (editValidation as SafeParseError<unknown>).error);
      } else {
        store.setRowError(rowId, null);
      }

      if (!hasChanges && !forceEdit) {
        console.debug('No changes to save.', rowId);
        return;
      } else if (syncWholeRow) {
        changes = newRow;
      }

      local = { ...newRow, status: 'loading' as const, isCreating: original.isDraft };
      store.setLocalRow(local);

      // Already resolved and recorded above, against the same merged row.
      const validation = editValidation;

      for (const issue of validation?.error?.issues ?? []) {
        if (issue.fatal) {
          delete changes[issue.path.join('.')];
        }
      }

      return {
        rowId,
        payload: changes,
        resolve: (accepted) => get().reconcileEdit(rowId, accepted),
        reject: (error) => {
          console.error(error);
          store.setRowError(rowId, error); // handle folding errors as well ? ?? ? ..maybe would be good to allow multiple errors per cell?
          store.setLocalRow({ ...get().getLocalRow(rowId), status: 'rejected', isCreating: false });
        },
        getLatestId: () => get().getLocalRow(rowId).id,
      };
    },
    remove: (rowId) => {
      const store = get();
      const original = store.localRows[rowId];

      if (original.isDeleted) {
        return;
      }

      set((state) => {
        state.localRows[rowId].status = 'deleting';
        state.localRows[rowId].isDeleted = true;
        delete state.rowErrors[rowId as number];
        state.rowUpdates.push({ rowId, _action: 'delete' });
      });

      // A draft the server has never seen has no row to DELETE: drop it locally, the
      // same way `resolve` does, and enqueue nothing. Without this the request went out
      // with an undefined id and the rejection below RESTORED the row the user deleted.
      // The `_action: 'delete'` row update above must already be queued — MUI is mounted
      // uncontrolled (`EditableDataGrid.tsx:397`), so that is what unpaints the row.
      // `useLocalEditableGridStore` guards this one layer up; the two grids that build
      // this slice directly (line-items, expenses) reach `remove` raw.
      if (isDraftAwaitingCreate(original)) {
        store.deleteLocal(rowId);
        return;
      }

      if (!canDeleteEntity(original)) {
        return;
      }

      return {
        rowId,
        // need a getter for db id
        payload: () => get().getLocalRow(rowId).id,
        resolve: () => {
          store.deleteLocal(rowId);
        },
        reject: (error) => {
          console.error(error, rowId);
          store.setLocalRow({ ...original, status: 'rejected', isDeleted: false });
        },
      };
    },
    setRowError: (rowId, error) => {
      set((state) => {
        if (error === null) {
          delete state.rowErrors[Number(rowId)];
          return;
        }

        state.rowErrors[Number(rowId)] = error;
      });
    },
    setLocalRow: (item) =>
      set((state) => {
        const rowId = key(item);

        if (!item.isDeleted) {
          state.rowUpdates.push({ ...item, _action: state.localRows[rowId] ? 'update' : 'create' });
        }

        state.localRows[rowId] = item;
      }),
    assignToLocalRows: (applyUpdate) => {
      const state = get();

      for (const rowId in state.localRows) {
        const row = state.localRows[rowId];
        const patch = applyUpdate(row) ?? {};
        const hasChange = Object.keys(patch).some(
          (k) => !_.isEqual(patch[k as keyof Partial<T>], row[k as keyof GridRow<T>])
        );
        if (hasChange) {
          state.setLocalRow({ ...row, ...patch });
        }
      }
    },
    getLocalRow: (rowId) => get().localRows[rowId],
    getLocalRows: () => {
      const localRows = get().localRows;

      return Object.values(localRows).filter((row) => !row.isDeleted);
    },
    getPatch: (rowId) => get().rowPatches[rowId],
    recordPatch: (row, patch) => {
      set((state) => {
        const rowId = key(row);
        state.rowPatches[rowId] = patch;
      });
    },
    recordSnapshot: (rowId, snapshot) => {
      set((state) => {
        state.rowSnapshots[rowId] = snapshot;
      });
    },
    deleteLocal: (rowId) => {
      set((state) => {
        delete state.localRows[rowId];
      });

      return true;
    },
    getRowError: (rowId: number) => get().rowErrors[rowId] ?? null,
    validateRows: (validationModel) => {
      lastValidationModel = validationModel;
      set((state) => {
        const errors: Record<number, Error> = {};

        Object.entries(get().localRows).forEach(([key, row]) => {
          // Soft-deleted rows are kept for sync but must not carry validation errors.
          if (row.isDeleted) {
            return;
          }

          const parsed = resolveValidationModel(validationModel, { row }).safeParse(row);

          if (!parsed.success) {
            errors[Number(key)] = (parsed as SafeParseError<unknown>).error;
          }
        });

        state.rowErrors = errors;
      });
    },
    revalidate: () => {
      if (lastValidationModel) {
        get().validateRows(lastValidationModel);
      }
    },
    getRemoteRowId: (rowId) => {
      const row = get().localRows[rowId];

      if (!row) {
        return null;
      }

      return row.id;
    },
    syncRowUpdates: () => {
      return {
        rowUpdates: get().rowUpdates,
        reset: () =>
          set((state) => {
            state.rowUpdates = [];
          }),
      };
    },
    recordInteraction: (rowId, interaction) => {
      set((state) => {
        if (!state.rowInteractionModel[rowId]) {
          state.rowInteractionModel[rowId] = { saved: false, visited: new Set() };
        }

        if (interaction.visited) {
          state.rowInteractionModel[rowId].visited.add(interaction.visited);
        }

        if (interaction.saved) {
          state.rowInteractionModel[rowId].saved = true;
        }
      });
    },
    findRow: (predicate) => {
      const localRows = get().localRows;

      return Object.values(localRows).find(predicate);
    },
  };
};
